import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { FeatherArrowLeft, FeatherCalendar } from '../../components/ui/icons';
import DateSelector from '../../components/DailyReview/DateSelector';
import ProjectCard from '../../components/DailyReview/ProjectCard';
import ResourcesTreeView from '../../components/DailyReview/ResourcesTreeView';
import dailyReviewService from '../../services/dailyReviewService';
import { releasesApi } from '../../services/api';
import { getTodayLocalDate } from '../../utils/date';
import VersionCheckBanner from '../../components/shared/VersionCheckBanner';
import './DailyWorkPlanReview.css';

const DailyWorkPlanReview = () => {
    const navigate = useNavigate();
    const [date, setDate] = useState(() => {
        return getTodayLocalDate();
    });
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showOnlyWithRemaining, setShowOnlyWithRemaining] = useState(false);
    const [resources, setResources] = useState([]);
    const [resourcesLoading, setResourcesLoading] = useState(true);
    const [resourcesError, setResourcesError] = useState(null);
    const [releasesData, setReleasesData] = useState({});

    useEffect(() => {
        fetchProjects();
        fetchResources();
    }, [date]);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await dailyReviewService.getProjects(date);
            const projectsData = data || [];
            setProjects(projectsData);
            
            // Fetch releases for all projects in parallel
            const releasesPromises = projectsData.map(async (project) => {
                try {
                    const releases = await releasesApi.getProjectReleases(project.id);
                    return { projectId: project.id, releases };
                } catch (err) {
                    console.error(`Error fetching releases for project ${project.id}:`, err);
                    return { projectId: project.id, releases: [] };
                }
            });
            
            const releasesResults = await Promise.all(releasesPromises);
            const releasesMap = releasesResults.reduce((map, result) => {
                map[result.projectId] = result.releases;
                return map;
            }, {});
            setReleasesData(releasesMap);
        } catch (err) {
            console.error('Error fetching projects:', err);
            setError('Failed to load projects. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchResources = async () => {
        try {
            setResourcesLoading(true);
            setResourcesError(null);
            const data = await dailyReviewService.getResourcesWithProjects(date);
            setResources(data || []);
        } catch (err) {
            console.error('Error fetching resources:', err);
            setResourcesError('Failed to load resources. Please try again.');
        } finally {
            setResourcesLoading(false);
        }
    };

    const handleDateChange = (newDate) => {
        setDate(newDate);
    };

    const { projectsWithTasks, projectsWithoutTasks } = useMemo(() => {
        // Separate projects by whether they have tasks in work plan
        const withTasks = projects.filter(project => (project.taskCount || 0) > 0);
        const withoutTasks = projects.filter(project => (project.taskCount || 0) === 0);

        // Apply filter to both groups independently
        const filterFn = (project) => {
            if (!showOnlyWithRemaining) return true;
            const remaining = project.pickupStats?.remaining || 0;
            return remaining > 0;
        };

        return {
            projectsWithTasks: withTasks.filter(filterFn),
            projectsWithoutTasks: withoutTasks.filter(filterFn)
        };
    }, [projects, showOnlyWithRemaining]);

    if (loading) {
        return (
            <>
                <VersionCheckBanner />
                <div className="daily-review-container">
                    <div className="daily-review-loading">
                        <div className="loading-spinner" />
                        <p>Loading projects...</p>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <VersionCheckBanner />
                <div className="daily-review-container">
                    <div className="daily-review-error">
                        <p>{error}</p>
                        <Button onClick={fetchProjects}>Retry</Button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <VersionCheckBanner />
            <div className="daily-review-container">
            <div className="daily-review-header">
                <div className="daily-review-header-top">
                    <Button
                        variant="neutral-tertiary"
                        icon={<FeatherArrowLeft />}
                        onClick={() => navigate('/dashboard')}
                    >
                        Back
                    </Button>
                    <h1 className="daily-review-title">Daily Work Plan Review</h1>
                </div>
                <div className="daily-review-header-actions">
                    <DateSelector date={date} onDateChange={handleDateChange} />
                </div>
            </div>

            <ResourcesTreeView
                resources={resources}
                loading={resourcesLoading}
                error={resourcesError}
            />

            <div className="daily-review-content">
                {projects.length > 0 ? (
                    <>
                        <div className="daily-review-controls">
                            <div className="daily-review-summary">
                                <p className="daily-review-summary-text">
                                    Found <strong>{projectsWithTasks.length}</strong> {projectsWithTasks.length === 1 ? 'project' : 'projects'} in progress today
                                    {projectsWithoutTasks.length > 0 && (
                                        <> and <strong>{projectsWithoutTasks.length}</strong> {projectsWithoutTasks.length === 1 ? 'project' : 'projects'} without tasks</>
                                    )}
                                    {showOnlyWithRemaining && ' (filtered)'}
                                    {' '}for {new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                </p>
                            </div>
                            <label className="filter-toggle">
                                <input
                                    type="checkbox"
                                    checked={showOnlyWithRemaining}
                                    onChange={(e) => setShowOnlyWithRemaining(e.target.checked)}
                                />
                                <span>Show only projects with remaining tasks</span>
                            </label>
                        </div>

                        {/* Section 1: Projects in Progress Today */}
                        <div className="daily-review-section">
                            <h2 className="daily-review-section-header">
                                Projects in Progress Today
                                <span className="daily-review-section-count">({projectsWithTasks.length})</span>
                            </h2>
                            {projectsWithTasks.length > 0 ? (
                                <div className="daily-review-projects-grid">
                                    {projectsWithTasks.map(project => (
                                        <ProjectCard key={project.id} project={project} date={date} releases={releasesData[project.id] || []} />
                                    ))}
                                </div>
                            ) : (
                                <div className="daily-review-section-empty">
                                    <p>No projects in progress today{showOnlyWithRemaining && ' with remaining tasks'}.</p>
                                </div>
                            )}
                        </div>

                        {/* Section 2: Projects Without Tasks in Work Plan */}
                        <div className="daily-review-section">
                            <h2 className="daily-review-section-header">
                                Projects Remaining
                                <span className="daily-review-section-count">({projectsWithoutTasks.length})</span>
                            </h2>
                            {projectsWithoutTasks.length > 0 ? (
                        <div className="daily-review-projects-grid">
                                    {projectsWithoutTasks.map(project => (
                                <ProjectCard key={project.id} project={project} date={date} releases={releasesData[project.id] || []} />
                            ))}
                                </div>
                            ) : (
                                <div className="daily-review-section-empty">
                                    <p>No projects without tasks in work plan{showOnlyWithRemaining && ' with remaining tasks'}.</p>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="daily-review-empty">
                        <FeatherCalendar size={48} className="daily-review-empty-icon" />
                        <h2 className="daily-review-empty-title">No Projects Found</h2>
                        <p className="daily-review-empty-text">
                            No projects found for {new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
                        </p>
                        <p className="daily-review-empty-text">
                            Try selecting a different date or check back later.
                        </p>
                    </div>
                )}
            </div>
        </div>
        </>
    );
};

export default DailyWorkPlanReview;
